import { useCallback, Fragment, useRef, useEffect } from 'react';

import { useComponentMutations } from 'approot/activity-builder/activity-builder.hooks';
import { ColumnsComponentsWrapper } from './columns-components';
import { ColumnAddMenu } from './column-add-menu';
import * as Styled from './columns.style';
import {
  COLUMN_ID_PREFIX,
  EditProps,
  HANDLE_ID_PREFIX,
} from './columns.constants';
import { Droppable } from 'approot/activity-builder/drag-drop/droppable';
import { AddComponentTemplate } from '../components-menu/components-menu.constants';
import { ComponentColumn } from '../component.constants';
import {
  sortComponents,
  getNewSortValue,
} from 'approot/activity-builder/activity-builder.utils';
import { useResizeColumns } from './columns-edit.hooks';
import { SNAP_SPANS, getClosestNumber } from './columns-edit.utils';
import ColumnsSettings from './settings/column-settings';
import {
  getAppliedColour,
  isBackgroundApplied,
  isBorderApplied,
} from './settings/column-settings.utils';
import { removeTypenameFromObject } from 'approot/shared/logic/remove-typename';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import { ACTIVITY_BUILDER_DRAWER_EMPTY_COLUMN_ADD } from 'approot/activity-builder/activity-builder.constants';
import { LANGUAGE_CODE_EN_AU } from 'global/constants';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';

const ColumnsEdit = ({
  id: columnComponentId,
  columns,
  columnProperties,
  sectionId,
  contentId,
  contentType,
  lessonId,
  type,
  sort,
  version,
}: EditProps) => {
  const columnsLastestRef = useRef(columns);

  const { isZoomToFit, slideScaleRatio, activeComponent, setActiveComponent } =
    useActivityBuilderContext();

  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const { handleAdd } = useComponentMutations({
    sectionId,
  });

  const handleAddComponent = useCallback(
    (props: AddComponentTemplate, column: ComponentColumn, index: number) => {
      const sortedComponents = sortComponents(column.components);
      const component = sortedComponents[index];
      const sort = getNewSortValue({
        components: sortedComponents,
        componentId: component?.id,
      });
      handleAdd({
        ...props,
        columnComponentId,
        columnId: column.columnId,
        sort,
      });
    },
    [columnComponentId, handleAdd]
  );

  const handleUpdateColspans = useCallback(
    (updatedWidths: number[]) => {
      const totalWidth = updatedWidths.reduce((accum, curr) => accum + curr, 0);
      const latestColumns = columnsLastestRef.current;

      if (!latestColumns) return;

      const columns = latestColumns.map((column, index) => {
        const colspan = Math.round(
          Number((updatedWidths[index] / totalWidth) * 12)
        );

        return {
          colspan: SNAP_SPANS.includes(colspan)
            ? colspan
            : getClosestNumber(colspan, SNAP_SPANS),
          alignment: column.alignment,
          style: column.style
            ? removeTypenameFromObject(column.style)
            : column.style,
          index: column.index,
          columnId: column.columnId,
        };
      });

      updateComponentProperties({
        input: {
          sectionId,
          componentId: columnComponentId,
          componentData: {
            properties: {
              columns,
            },
          },
        },
        typename: 'ActivitiesColumnsComponent',
        isSaveImmediate: true,
        onCompleted: () => {
          setWidths(undefined);
          newWidthsToSave.current = undefined;
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnComponentId, sectionId, updateComponentProperties]
  );

  const { widths, setWidths, newWidthsToSave } = useResizeColumns({
    handleUpdateColspans,
    columns,
    columnComponentId,
    slideScaleRatio,
  });

  // allows handleUpdateColspans to have the latest column settings
  useEffect(() => {
    columnsLastestRef.current = columns;
  }, [columns]);

  const settings = (
    <ColumnsSettings
      version={version}
      language={LANGUAGE_CODE_EN_AU}
      id={columnComponentId}
      sort={sort}
      type={type}
      columnProperties={columnProperties}
      columns={columns}
      sectionId={sectionId}
    />
  );

  const style = columnProperties?.style;

  return (
    <Styled.ColumnsContainerEdit
      data-selector="columns-container"
      $colours={getAppliedColour(style)}
      $showBorder={isBorderApplied(style)}
      $showBackground={isBackgroundApplied(style)}
      $marginBottom={0}
      $isColumnsEmpty={columns?.every(col => col.components.length === 0)}
      $turnOffResponsive={isZoomToFit}
    >
      {columns?.map((column, index) => (
        <Fragment key={column.columnId}>
          <Styled.ColumnsColumnEdit
            id={`${COLUMN_ID_PREFIX}${column.columnId}`}
            style={{
              width: !widths ? undefined : `${widths[index]}px`,
            }}
            $colspan={column.colspan}
            $alignment={column.alignment}
            $colours={getAppliedColour(column.style)}
            $showBorder={isBorderApplied(column.style)}
            $showBackground={isBackgroundApplied(column.style)}
            $turnOffResponsive={isZoomToFit}
          >
            <Styled.ColumnInner>
              {column.components.length > 0 ? (
                <ColumnsComponentsWrapper
                  id={columnComponentId}
                  column={column}
                  sectionId={sectionId}
                  contentId={contentId}
                  lessonId={lessonId}
                  settings={settings}
                  addComponent={(props, index) =>
                    handleAddComponent(props, column, index)
                  }
                  contentType={contentType}
                />
              ) : (
                <Styled.ColumnEmpty
                  $isActive={
                    activeComponent?.id ===
                      ACTIVITY_BUILDER_DRAWER_EMPTY_COLUMN_ADD &&
                    activeComponent.column?.id === column.columnId
                  }
                >
                  <Droppable
                    column={{
                      id: column.columnId,
                      componentId: columnComponentId,
                    }}
                    sectionId={sectionId}
                    sortedComponents={[]}
                    isFixedPosition
                    label="Add Item"
                  />
                  <ColumnAddMenu
                    canRenderMenu={
                      activeComponent?.id ===
                        ACTIVITY_BUILDER_DRAWER_EMPTY_COLUMN_ADD &&
                      activeComponent.column?.id === column.columnId
                    }
                    addComponentToColumn={componentProps =>
                      handleAddComponent(componentProps, column, 0)
                    }
                    render={onClick => (
                      <SecondaryButton
                        type="button"
                        $isSmall
                        onClick={e => {
                          // focus on the empty column
                          setActiveComponent({
                            id: ACTIVITY_BUILDER_DRAWER_EMPTY_COLUMN_ADD,
                            column: {
                              componentId: columnComponentId,
                              id: column.columnId,
                            },
                          });
                          onClick(e);
                        }}
                        data-selector="btn-add-item"
                      >
                        Add Item
                      </SecondaryButton>
                    )}
                  />
                </Styled.ColumnEmpty>
              )}
              {index !== columns.length - 1 &&
                columns.length !== 7 &&
                columns.length !== 5 && (
                  <Styled.ColumnsTooltipWrapper hoverText="Resize columns">
                    <Styled.ColumnsHandle
                      aria-label="Click and drag to resize columns"
                      id={`${HANDLE_ID_PREFIX}${index}`}
                    />
                  </Styled.ColumnsTooltipWrapper>
                )}
            </Styled.ColumnInner>
          </Styled.ColumnsColumnEdit>
        </Fragment>
      ))}
    </Styled.ColumnsContainerEdit>
  );
};

export default ColumnsEdit;
